import { FormControlLabel, Switch } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import styled from 'styled-components';
import { Constants } from '../../../common/constants';
import { DailyData } from '../../../common/model/dto/daily-data';
import { UserInfo } from '../../../common/model/dto/user-info';
import { UserCycle } from '../../../common/model/dto/user-cycle/user-cycle';
import { ChartRangeMode } from '../../../common/model/type/chart-range-selection.type';
import { DateRange } from '../../../common/model/type/date-range.type';
import { HormoneValueType } from '../../../common/model/type/hormone-values.type';
import ChartData from '../../../common/model/type/chart-data.type';
import { ScanChartData } from '../../../common/model/type/scan-chart-data.type';
import ChartUtils from '../../../common/utils/services/chart-utils';
import DateUtils from '../../../common/utils/services/date-utils';
import {
  HORMONE_TRENDS_DATE_TICK_LABEL_WIDTH,
  HORMONE_TRENDS_MIN_DAY_COLUMN_WIDTH,
  HORMONE_SERIES_ORDER,
  HORMONE_TRENDS_SERIES,
  HORMONE_TRENDS_SERIES_ORDER,
  HORMONE_TRENDS_SERIES_STYLES,
  buildDayColumnModel,
  getBbtDomain,
  getHormoneAxisColour,
  getHormoneAxisOrientation,
  getLeftGutterWidth,
  getRightGutterWidth,
  getVisibleHormoneSeries,
} from '../../../common/utils/services/hormone-trends-utils';
import {
  CyclePhaseDays,
  DayColumn,
  HormoneSeriesId,
  HormoneTrendsPlotDatum,
  HormoneTrendsSeriesId,
} from '../../../common/model/type/hormone-trends.type';
import MathUtils from '../../../common/utils/services/math-utils';
import Colours from '../../../design-system/colours';
import OvChartLabel from '../atoms/OvChartLabel';
import OvCyclePhaseStrip from '../molecules/OvCyclePhaseStrip';
import OvHormoneSeriesChips from '../molecules/OvHormoneSeriesChips';
import OvHormoneReferenceRanges from '../molecules/OvHormoneReferenceRanges';
import OvHormoneDetails from '../molecules/OvHormoneDetails';
import OvHormoneTrendsInteractionLayer, {
  HormoneTrendsTooltipValues,
} from '../molecules/OvHormoneTrendsInteractionLayer';
import OvSymptomGrid from '../molecules/OvSymptomGrid';

export interface OvHormoneTrendsProps {
  dateRange: DateRange;
  rangeMode: ChartRangeMode;
  selectedCycle?: UserCycle;
  dailyDataArray?: DailyData[];
  phaseDays?: CyclePhaseDays;
  user?: UserInfo;
}

const toTooltipValues = (
  datum: HormoneTrendsPlotDatum
): HormoneTrendsTooltipValues => ({
  lh: datum.lh,
  pg: datum.pg,
  lr_e3g: datum.lr_e3g,
  bbt: datum.bbt,
});

const toDetailsData = (column: DayColumn): ChartData => ({
  index: column.cycleDay ?? column.index + 1,
  dailyData: column.dailyData,
  dateStr: column.dateLabel,
  measured: !!column.dailyData,
  value: column.dailyData?.lh ?? 0,
  scansChartData: [],
});

/** No hormone focused: the chart opens showing every default series at once. */
const PLOT_HEIGHT = 300;

const MAX_X_AXIS_TICKS = 31;

/**
 * Clamps, not domains. `MathUtils.normalizeValue` bounds a reading to what the
 * assay can report; the axis then runs past that so the top of the scale is not
 * also the top of the plot. The two numbers differ on every hormone.
 */
const HORMONE_CLAMPS: Record<HormoneSeriesId, [number, number]> = {
  lh: [Constants.LH_MIN, Constants.LH_MAX],
  pg: [Constants.PG_MIN, Constants.PG_MAX],
  lr_e3g: [Constants.LR_E3G_MIN, Constants.LR_E3G_MAX],
};

const HORMONE_VALUE_TYPES: Record<HormoneSeriesId, HormoneValueType> = {
  lh: HormoneValueType.LH,
  pg: HormoneValueType.PG,
  lr_e3g: HormoneValueType.LR_E3G,
};

const toHormoneValue = (
  seriesId: HormoneSeriesId,
  dailyData?: DailyData
): number | null => {
  const raw = dailyData?.[seriesId];

  if (typeof raw !== 'number') {
    return null;
  }

  const [min, max] = HORMONE_CLAMPS[seriesId];

  return MathUtils.normalizeValue(raw, min, max);
};

const toPlotDatum = (column: DayColumn): HormoneTrendsPlotDatum => ({
  index: column.index,
  lh: toHormoneValue('lh', column.dailyData),
  pg: toHormoneValue('pg', column.dailyData),
  lr_e3g: toHormoneValue('lr_e3g', column.dailyData),
  bbt: column.dailyData?.journal_entry?.temperature ?? null,
});

const buildScanPoints = (
  columns: DayColumn[]
): Record<HormoneSeriesId, ScanChartData[]> =>
  HORMONE_SERIES_ORDER.reduce((points, seriesId) => {
    const [min, max] = HORMONE_CLAMPS[seriesId];

    points[seriesId] = columns.flatMap((column) =>
      ChartUtils.mapScansToScanChartData(
        min,
        max,
        column.index,
        HORMONE_VALUE_TYPES[seriesId],
        column.dailyData
      )
    );

    return points;
  }, {} as Record<HormoneSeriesId, ScanChartData[]>);

const OvHormoneTrends: FC<OvHormoneTrendsProps> = ({
  dateRange,
  rangeMode,
  selectedCycle,
  dailyDataArray,
  phaseDays,
  user,
}) => {
  const { t } = useTranslation();
  const [focusedSeriesId, setFocusedSeriesId] =
    useState<HormoneTrendsSeriesId | null>(null);
  const [bbtEnabled, setBbtEnabled] = useState(false);
  const [showAllScans, setShowAllScans] = useState(false);
  const [selectedColumnKey, setSelectedColumnKey] = useState<string>();

  const { columns, phaseBands, symptomRows } = useMemo(
    () =>
      buildDayColumnModel({
        dateRange,
        rangeMode,
        selectedCycle,
        dailyDataArray,
        phaseDays,
      }),
    [dailyDataArray, dateRange, phaseDays, rangeMode, selectedCycle]
  );

  useEffect(() => {
    setSelectedColumnKey(undefined);
  }, [dateRange.endDate, dateRange.startDate, rangeMode, selectedCycle?.id]);

  const plotData = useMemo(() => columns.map(toPlotDatum), [columns]);
  const tooltipValues = useMemo(
    () => plotData.map(toTooltipValues),
    [plotData]
  );
  const scanPoints = useMemo(() => buildScanPoints(columns), [columns]);
  const bbtDomain = useMemo(() => getBbtDomain(columns), [columns]);
  const periodDays = useMemo(
    () => ChartUtils.getPeriodDays(columns.map((column) => column.dailyData)),
    [columns]
  );
  const visibleSeries = useMemo(
    () => getVisibleHormoneSeries(focusedSeriesId, bbtEnabled),
    [focusedSeriesId, bbtEnabled]
  );
  const tickIndexes = useMemo(() => {
    const minTickSpacing =
      rangeMode === 'cycle'
        ? 1
        : Math.max(
            1,
            Math.ceil(
              HORMONE_TRENDS_DATE_TICK_LABEL_WIDTH /
                HORMONE_TRENDS_MIN_DAY_COLUMN_WIDTH
            )
          );
    const step = Math.max(
      minTickSpacing,
      Math.ceil(columns.length / MAX_X_AXIS_TICKS)
    );

    return columns
      .filter((column) => column.index % step === 0)
      .map((column) => column.index);
  }, [columns, rangeMode]);

  const formatDayTick = (index: number): string => {
    const column = columns[index];

    if (!column) {
      return '';
    }

    return rangeMode === 'cycle' && column.cycleDay !== undefined
      ? `${column.cycleDay}`
      : DateUtils.formatDate(column.date, 'MMM D');
  };

  const leftGutterWidth = getLeftGutterWidth(focusedSeriesId);
  const rightGutterWidth = getRightGutterWidth(focusedSeriesId);

  const scrollUnitMinWidth =
    leftGutterWidth +
    rightGutterWidth +
    columns.length * HORMONE_TRENDS_MIN_DAY_COLUMN_WIDTH;
  const selectedColumn = columns.find(
    (column) => column.key === selectedColumnKey
  );

  return (
    <Container>
      <Header>
        <OvChartLabel>{t('hormoneTrends.title')}</OvChartLabel>
        <FormControlLabel
          checked={showAllScans}
          onChange={(_event, checked: boolean) => setShowAllScans(checked)}
          control={<StyledSwitch />}
          label={<OvChartLabel>{t('dashboard.showAllScans')}</OvChartLabel>}
          labelPlacement="start"
        />
      </Header>

      <OvHormoneSeriesChips
        focusedSeriesId={focusedSeriesId}
        onFocusChange={setFocusedSeriesId}
        bbtEnabled={bbtEnabled}
        onBbtToggle={() => setBbtEnabled((enabled) => !enabled)}
      />

      {columns.length > 0 && (
        <InteractionLayout>
          <TrendsContent>
            <ScrollContainer>
              <ScrollUnit
                data-testid="hormone-trends-scroll-unit"
                data-column-count={columns.length}
                style={{ minWidth: `${scrollUnitMinWidth}px` }}
              >
                {rangeMode === 'cycle' && (
                  <OvCyclePhaseStrip
                    columnCount={columns.length}
                    phaseBands={phaseBands}
                    leftGutterWidth={leftGutterWidth}
                    rightGutterWidth={rightGutterWidth}
                  />
                )}
                <OvHormoneTrendsInteractionLayer
                  columns={columns}
                  selectedSeries={visibleSeries}
                  selectedIndex={selectedColumn?.index}
                  tooltipValues={tooltipValues}
                  onSelectColumn={(column) =>
                    setSelectedColumnKey((current) =>
                      current === column.key ? undefined : column.key
                    )
                  }
                  leftGutterWidth={leftGutterWidth}
                  rightGutterWidth={rightGutterWidth}
                >
                  <ChartRegion
                    data-testid="hormone-trends-chart"
                    data-column-count={columns.length}
                    data-column-keys={columns
                      .map((column) => column.key)
                      .join(',')}
                  >
                    <ResponsiveContainer width="100%" height={PLOT_HEIGHT}>
                      <LineChart
                        data={plotData}
                        margin={{ top: 16, right: 0, bottom: 0, left: 0 }}
                      >
                        <CartesianGrid
                          vertical={false}
                          stroke={Colours.OV_BORDER_COLOR_V3}
                        />
                        <XAxis
                          type="number"
                          dataKey="index"
                          domain={[-0.5, columns.length - 0.5]}
                          ticks={tickIndexes}
                          tickFormatter={formatDayTick}
                          interval={0}
                          tickLine={false}
                          tick={{ fontSize: 11, fill: Colours.OV_BASE }}
                          height={rangeMode === 'cycle' ? 44 : 32}
                          label={
                            rangeMode === 'cycle'
                              ? {
                                  value: t('hormoneTrends.axis.cycleDay'),
                                  position: 'insideBottom',
                                  fontSize: 11,
                                  fill: Colours.OV_BASE,
                                }
                              : undefined
                          }
                        />
                        {HORMONE_TRENDS_SERIES_ORDER.map((seriesId) => {
                          const series = HORMONE_TRENDS_SERIES[seriesId];
                          const isVisible = visibleSeries.includes(seriesId);
                          const axisColour = getHormoneAxisColour(seriesId);

                          return (
                            <YAxis
                              key={seriesId}
                              yAxisId={series.axisId}
                              orientation={getHormoneAxisOrientation(
                                seriesId,
                                focusedSeriesId
                              )}
                              width={series.axisWidth}
                              domain={
                                seriesId === 'bbt' ? bbtDomain : series.domain
                              }
                              allowDataOverflow
                              tickLine={false}
                              axisLine={isVisible}
                              tick={{
                                fontSize: 11,
                                fill: isVisible ? axisColour : 'transparent',
                              }}
                            />
                          );
                        })}
                        {periodDays.periodEndingDayIndexes.map(
                          (endIndex, index) => {
                            const startIndex =
                              periodDays.periodStartDayIndexes[index];

                            return (
                              <ReferenceArea
                                key={`${startIndex}-${endIndex}`}
                                yAxisId={HORMONE_TRENDS_SERIES.lh.axisId}
                                x1={Math.max(startIndex - 0.5, -0.5)}
                                x2={Math.min(
                                  endIndex + 0.5,
                                  columns.length - 0.5
                                )}
                                fill={Colours.OV_PERIOD_PINK}
                                fillOpacity={0.35}
                              />
                            );
                          }
                        )}
                        {visibleSeries.map((seriesId) => {
                          const style = HORMONE_TRENDS_SERIES_STYLES[seriesId];

                          return (
                            <Line
                              key={seriesId}
                              yAxisId={HORMONE_TRENDS_SERIES[seriesId].axisId}
                              dataKey={seriesId}
                              name={HORMONE_TRENDS_SERIES[seriesId].displayName}
                              type="monotoneX"
                              isAnimationActive={false}
                              connectNulls
                              stroke={style.colour}
                              strokeWidth={2}
                              strokeDasharray={style.strokeDasharray}
                              dot={{
                                r: 2.5,
                                fill: style.colour,
                                strokeWidth: 0,
                              }}
                              activeDot={false}
                            />
                          );
                        })}
                        {showAllScans &&
                          HORMONE_SERIES_ORDER.filter(
                            (seriesId) =>
                              visibleSeries.includes(seriesId) &&
                              scanPoints[seriesId].length > 0
                          ).map((seriesId) => (
                            <Line
                              key={`scans-${seriesId}`}
                              yAxisId={HORMONE_TRENDS_SERIES[seriesId].axisId}
                              data={scanPoints[seriesId]}
                              dataKey="value"
                              isAnimationActive={false}
                              strokeOpacity={0}
                              dot={{
                                r: 2.5,
                                fill: HORMONE_TRENDS_SERIES_STYLES[seriesId]
                                  .colour,
                                fillOpacity: 0.5,
                                strokeWidth: 0,
                              }}
                              activeDot={false}
                            />
                          ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </ChartRegion>
                  <OvSymptomGrid
                    columns={columns}
                    rows={symptomRows}
                    leftGutterWidth={leftGutterWidth}
                    rightGutterWidth={rightGutterWidth}
                  />
                </OvHormoneTrendsInteractionLayer>
              </ScrollUnit>
            </ScrollContainer>
            <OvHormoneReferenceRanges />
          </TrendsContent>
          {selectedColumn && (
            <DetailsContainer data-testid="hormone-trends-details-panel">
              <OvHormoneDetails
                data={toDetailsData(selectedColumn)}
                healthGoal={user?.health_goal}
                heading={
                  selectedColumn.cycleDay === undefined
                    ? selectedColumn.dateLabel
                    : `${t('dashboard.chart.day')} ${
                        selectedColumn.cycleDay
                      } - ${selectedColumn.dateLabel}`
                }
                onClose={() => setSelectedColumnKey(undefined)}
              />
            </DetailsContainer>
          )}
        </InteractionLayout>
      )}
    </Container>
  );
};

export default OvHormoneTrends;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const ScrollContainer = styled.div`
  overflow-x: auto;
  width: 100%;
`;

const TrendsContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const InteractionLayout = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
`;

const DetailsContainer = styled.aside`
  flex: 0 0 17rem;
  margin-left: 1.5rem;
  word-break: break-word;
`;

const ScrollUnit = styled.div`
  width: 100%;
`;

const ChartRegion = styled.div`
  width: 100%;
`;

const StyledSwitch = styled(Switch)`
  && {
    .MuiSwitch-thumb {
      color: ${Colours.OV_BASE};
    }

    .MuiSwitch-track {
      background-color: ${Colours.CD_TOOLTIP_HEADER} !important;
    }
  }
`;
